import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  List,
  message,
  Modal,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  Tooltip,
  Statistic,
  Badge,
  Collapse,
  Timeline,
} from "antd";
import {
  EyeOutlined,
  DollarOutlined,
  UserOutlined,
  CalendarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  HistoryOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { ISalaryPricingLog, SalaryChangeType } from "../../types/salaryPricingLog.type";
import { salaryPricingLogApi } from "../../api/salaryPricingLogApi";
import dayjs from "dayjs";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

interface WorkerSalaryHistoryProps {
  workerId: string;
  workerName: string;
  workerSurname?: string;
  visible: boolean;
  onClose: () => void;
}

export default function WorkerSalaryHistory({
  workerId,
  workerName,
  workerSurname,
  visible,
  onClose,
}: WorkerSalaryHistoryProps) {
  const [logs, setLogs] = useState<ISalaryPricingLog[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedLog, setSelectedLog] = useState<ISalaryPricingLog | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  // Загрузка данных
  const fetchWorkerHistory = async () => {
    setLoading(true);
    try {
      const response = await salaryPricingLogApi.getWorkerSalaryHistory(workerId, {
        page,
        limit: pageSize,
      });
      setLogs(response.logs);
      setTotal(response.total);
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при загрузке истории");
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkerStats = async () => {
    setStatsLoading(true);
    try {
      const response = await salaryPricingLogApi.getSalaryPricingStats({
        workerId,
      });
      setStats(response);
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при загрузке статистики");
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    if (visible && workerId) {
      fetchWorkerHistory();
      fetchWorkerStats();
    }
  }, [visible, workerId, page, pageSize]);

  // Просмотр записи
  const handleViewLog = (log: ISalaryPricingLog) => {
    setSelectedLog(log);
    setIsViewModalOpen(true);
  };

  // Получение цвета для типа изменения
  const getChangeTypeColor = (changeType: SalaryChangeType) => {
    switch (changeType) {
      case SalaryChangeType.MANUAL:
        return "blue";
      case SalaryChangeType.SALARY_REQUEST:
        return "green";
      case SalaryChangeType.SYSTEM:
        return "orange";
      default:
        return "default";
    }
  };

  // Получение текста для типа изменения
  const getChangeTypeText = (changeType: SalaryChangeType) => {
    switch (changeType) {
      case SalaryChangeType.MANUAL:
        return "Ручное изменение";
      case SalaryChangeType.SALARY_REQUEST:
        return "Запрос на увеличение";
      case SalaryChangeType.SYSTEM:
        return "Системное изменение";
      default:
        return "Неизвестно";
    }
  };

  // Получение иконки для типа изменения
  const getChangeTypeIcon = (changeType: SalaryChangeType) => {
    switch (changeType) {
      case SalaryChangeType.MANUAL:
        return <FileTextOutlined />;
      case SalaryChangeType.SALARY_REQUEST:
        return <CheckCircleOutlined />;
      case SalaryChangeType.SYSTEM:
        return <HistoryOutlined />;
      default:
        return <FileTextOutlined />;
    }
  };

  // Колонки таблицы
  const columns = [
    {
      title: "Дата",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => (
        <Space>
          <CalendarOutlined />
          <Text>{dayjs(date).format("DD.MM.YYYY HH:mm")}</Text>
        </Space>
      ),
    },
    {
      title: "Тип изменения",
      dataIndex: "changeType",
      key: "changeType",
      render: (changeType: SalaryChangeType) => (
        <Tag color={getChangeTypeColor(changeType)} icon={getChangeTypeIcon(changeType)}>
          {getChangeTypeText(changeType)}
        </Tag>
      ),
    },
    {
      title: "Изменение зарплаты",
      dataIndex: "salaryDifference",
      key: "salaryDifference",
      render: (difference: number, record: ISalaryPricingLog) => (
        <Space direction="vertical" size="small">
          <Space>
            {difference > 0 ? (
              <ArrowUpOutlined style={{ color: "#52c41a" }} />
            ) : (
              <ArrowDownOutlined style={{ color: "#ff4d4f" }} />
            )}
            <Text strong style={{ color: difference > 0 ? "#52c41a" : "#ff4d4f" }}>
              {difference > 0 ? "+" : ""}{difference.toLocaleString()} {record.salaryCurrency}
            </Text>
          </Space>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.totalOldSalary.toLocaleString()} → {record.totalNewSalary.toLocaleString()}
          </Text>
        </Space>
      ),
    },
    {
      title: "Изменение долга",
      dataIndex: "debtDifference",
      key: "debtDifference",
      render: (difference: number, record: ISalaryPricingLog) => {
        if (difference === undefined || difference === null) return "-";
        return (
          <Space>
            {difference > 0 ? (
              <ArrowUpOutlined style={{ color: "#faad14" }} />
            ) : (
              <ArrowDownOutlined style={{ color: "#52c41a" }} />
            )}
            <Text strong style={{ color: difference > 0 ? "#faad14" : "#52c41a" }}>
              {difference > 0 ? "+" : ""}{difference.toLocaleString()} {record.salaryCurrency}
            </Text>
          </Space>
        );
      },
    },
    {
      title: "Причина",
      dataIndex: "reason",
      key: "reason",
      render: (reason: string) => (
        <Tooltip title={reason}>
          <Text style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {reason}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Изменено",
      dataIndex: "changedBy",
      key: "changedBy",
      render: (changedBy: any, record: ISalaryPricingLog) => (
        <Space>
          <UserOutlined />
          <Text>{changedBy?.username || record.changedByName}</Text>
        </Space>
      ),
    },
    {
      title: "Действия",
      key: "actions",
      render: (record: ISalaryPricingLog) => (
        <Space>
          <Tooltip title="Просмотр деталей">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewLog(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <HistoryOutlined />
          История изменений зарплат: {workerName} {workerSurname || ""}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1200}
      footer={null}
    >
      {/* Статистика работника */}
      <Row gutter={16} style={{ marginBottom: "24px" }}>
        <Col span={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="Всего изменений"
              value={stats?.overallStats.totalChanges || 0}
              prefix={<BarChartOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="Общее увеличение"
              value={stats?.overallStats.totalSalaryIncrease || 0}
              valueStyle={{ color: "#52c41a" }}
              prefix={<ArrowUpOutlined />}
              suffix="сум"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="Общее уменьшение"
              value={Math.abs(stats?.overallStats.totalSalaryDecrease || 0)}
              valueStyle={{ color: "#ff4d4f" }}
              prefix={<ArrowDownOutlined />}
              suffix="сум"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="Среднее изменение"
              value={stats?.overallStats.avgSalaryChange || 0}
              valueStyle={{ color: "#1890ff" }}
              prefix={<DollarOutlined />}
              suffix="сум"
            />
          </Card>
        </Col>
      </Row>

      {/* Таблица истории */}
      <Table
        columns={columns}
        dataSource={logs}
        rowKey="_id"
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (page, pageSize) => {
            setPage(page);
            setPageSize(pageSize);
          },
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} записей`,
        }}
        size="small"
      />

      {/* Модальное окно просмотра деталей */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            Детали изменения зарплаты
          </Space>
        }
        open={isViewModalOpen}
        onCancel={() => setIsViewModalOpen(false)}
        width={900}
        footer={null}
      >
        {selectedLog && (
          <div>
            <Descriptions bordered column={2} size="small" style={{ marginBottom: "24px" }}>
              <Descriptions.Item label="Тип изменения">
                <Tag color={getChangeTypeColor(selectedLog.changeType)} icon={getChangeTypeIcon(selectedLog.changeType)}>
                  {getChangeTypeText(selectedLog.changeType)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Дата изменения">
                {dayjs(selectedLog.createdAt).format("DD.MM.YYYY HH:mm")}
              </Descriptions.Item>
              <Descriptions.Item label="Старая зарплата">
                <Space>
                  <DollarOutlined />
                  <Text strong>{selectedLog.totalOldSalary.toLocaleString()} {selectedLog.salaryCurrency}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Новая зарплата">
                <Space>
                  <DollarOutlined />
                  <Text strong>{selectedLog.totalNewSalary.toLocaleString()} {selectedLog.salaryCurrency}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Изменение зарплаты">
                <Space>
                  {selectedLog.salaryDifference > 0 ? (
                    <ArrowUpOutlined style={{ color: "#52c41a" }} />
                  ) : (
                    <ArrowDownOutlined style={{ color: "#ff4d4f" }} />
                  )}
                  <Text strong style={{ color: selectedLog.salaryDifference > 0 ? "#52c41a" : "#ff4d4f" }}>
                    {selectedLog.salaryDifference > 0 ? "+" : ""}{selectedLog.salaryDifference.toLocaleString()} {selectedLog.salaryCurrency}
                  </Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Изменение долга">
                {selectedLog.debtDifference !== undefined ? (
                  <Space>
                    {selectedLog.debtDifference > 0 ? (
                      <ArrowUpOutlined style={{ color: "#faad14" }} />
                    ) : (
                      <ArrowDownOutlined style={{ color: "#52c41a" }} />
                    )}
                    <Text strong style={{ color: selectedLog.debtDifference > 0 ? "#faad14" : "#52c41a" }}>
                      {selectedLog.debtDifference > 0 ? "+" : ""}{selectedLog.debtDifference.toLocaleString()} {selectedLog.salaryCurrency}
                    </Text>
                  </Space>
                ) : (
                  <Text type="secondary">Нет данных</Text>
                )}
              </Descriptions.Item>
              {selectedLog.relatedRequestId && (
                <Descriptions.Item label="Связанный запрос" span={2}>
                  <Space>
                    <CalendarOutlined />
                    <Text strong>{selectedLog.relatedRequestId.month}</Text>
                    <Badge 
                      status={selectedLog.relatedRequestId.status === "approved" ? "success" : 
                              selectedLog.relatedRequestId.status === "rejected" ? "error" : "processing"} 
                      text={selectedLog.relatedRequestId.status === "approved" ? "Одобрен" :
                            selectedLog.relatedRequestId.status === "rejected" ? "Отклонен" : "Ожидает"}
                    />
                  </Space>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Причина" span={2}>
                <div style={{ 
                  padding: "8px 12px", 
                  backgroundColor: "#f0f9ff", 
                  border: "1px solid #91d5ff", 
                  borderRadius: "6px",
                  color: "#1890ff"
                }}>
                  {selectedLog.reason}
                </div>
              </Descriptions.Item>
            </Descriptions>

            <Divider>
              <Space>
                <FileTextOutlined />
                Детали изменений ({selectedLog.changes.length})
              </Space>
            </Divider>
            
            <Collapse size="small">
              {selectedLog.changes.map((change, index) => (
                <Panel 
                  key={index} 
                  header={
                    <Space>
                      <Text strong>{change.forName}</Text>
                      {change.oldPerUnit !== undefined && change.newPerUnit !== undefined && (
                        <Tag color={(change.newPerUnit || 0) > (change.oldPerUnit || 0) ? "green" : "red"}>
                          {change.oldPerUnit || 0} → {change.newPerUnit || 0}
                        </Tag>
                      )}
                      {change.oldPerMonth !== undefined && change.newPerMonth !== undefined && (
                        <Tag color={(change.newPerMonth || 0) > (change.oldPerMonth || 0) ? "green" : "red"}>
                          {change.oldPerMonth || 0} → {change.newPerMonth || 0}
                        </Tag>
                      )}
                      {change.oldPerUnit === undefined && change.newPerUnit !== undefined && (
                        <Tag color="green">+{change.newPerUnit || 0}</Tag>
                      )}
                      {change.oldPerMonth === undefined && change.newPerMonth !== undefined && (
                        <Tag color="green">+{change.newPerMonth || 0}</Tag>
                      )}
                      {change.oldPerUnit !== undefined && change.newPerUnit === undefined && (
                        <Tag color="red">-{change.oldPerUnit || 0}</Tag>
                      )}
                      {change.oldPerMonth !== undefined && change.newPerMonth === undefined && (
                        <Tag color="red">-{change.oldPerMonth || 0}</Tag>
                      )}
                    </Space>
                  }
                >
                  <Descriptions column={2} size="small">
                    {(change.oldPerUnit !== undefined || change.newPerUnit !== undefined) && (
                      <Descriptions.Item label="Ставка за единицу">
                        {change.oldPerUnit !== undefined && change.newPerUnit !== undefined ? (
                          <div>
                            <div style={{ marginBottom: "4px" }}>
                              <Text type="secondary" style={{ fontSize: "12px" }}>Было:</Text>
                              <Text strong style={{ color: "#ff4d4f", marginLeft: "8px" }}>
                                {change.oldPerUnit.toLocaleString()}
                              </Text>
                            </div>
                            <div>
                              <Text type="secondary" style={{ fontSize: "12px" }}>Стало:</Text>
                              <Text strong style={{ color: "#52c41a", marginLeft: "8px" }}>
                                {(change.newPerUnit || 0).toLocaleString()}
                              </Text>
                            </div>
                          </div>
                        ) : change.oldPerUnit !== undefined ? (
                          <div>
                            <Text type="secondary" style={{ fontSize: "12px" }}>Удалено:</Text>
                            <Text strong style={{ color: "#ff4d4f", marginLeft: "8px" }}>
                              {change.oldPerUnit.toLocaleString()}
                            </Text>
                          </div>
                        ) : (
                          <div>
                            <Text type="secondary" style={{ fontSize: "12px" }}>Добавлено:</Text>
                            <Text strong style={{ color: "#52c41a", marginLeft: "8px" }}>
                              {(change.newPerUnit || 0).toLocaleString()}
                            </Text>
                          </div>
                        )}
                      </Descriptions.Item>
                    )}
                    {(change.oldPerMonth !== undefined || change.newPerMonth !== undefined) && (
                      <Descriptions.Item label="Ставка за месяц">
                        {change.oldPerMonth !== undefined && change.newPerMonth !== undefined ? (
                          <div>
                            <div style={{ marginBottom: "4px" }}>
                              <Text type="secondary" style={{ fontSize: "12px" }}>Было:</Text>
                              <Text strong style={{ color: "#ff4d4f", marginLeft: "8px" }}>
                                {change.oldPerMonth.toLocaleString()}
                              </Text>
                            </div>
                            <div>
                              <Text type="secondary" style={{ fontSize: "12px" }}>Стало:</Text>
                              <Text strong style={{ color: "#52c41a", marginLeft: "8px" }}>
                                {(change.newPerMonth || 0).toLocaleString()}
                              </Text>
                            </div>
                          </div>
                        ) : change.oldPerMonth !== undefined ? (
                          <div>
                            <Text type="secondary" style={{ fontSize: "12px" }}>Удалено:</Text>
                            <Text strong style={{ color: "#ff4d4f", marginLeft: "8px" }}>
                              {change.oldPerMonth.toLocaleString()}
                            </Text>
                          </div>
                        ) : (
                          <div>
                            <Text type="secondary" style={{ fontSize: "12px" }}>Добавлено:</Text>
                            <Text strong style={{ color: "#52c41a", marginLeft: "8px" }}>
                              {(change.newPerMonth || 0).toLocaleString()}
                            </Text>
                          </div>
                        )}
                      </Descriptions.Item>
                    )}
                    {(change.oldQuantity !== undefined || change.newQuantity !== undefined) && (
                      <Descriptions.Item label="Количество">
                        {change.oldQuantity !== undefined && change.newQuantity !== undefined ? (
                          <div>
                            <div style={{ marginBottom: "4px" }}>
                              <Text type="secondary" style={{ fontSize: "12px" }}>Было:</Text>
                              <Text strong style={{ color: "#ff4d4f", marginLeft: "8px" }}>
                                {change.oldQuantity}
                              </Text>
                            </div>
                            <div>
                              <Text type="secondary" style={{ fontSize: "12px" }}>Стало:</Text>
                              <Text strong style={{ color: "#52c41a", marginLeft: "8px" }}>
                                {change.newQuantity}
                              </Text>
                            </div>
                          </div>
                        ) : change.oldQuantity !== undefined ? (
                          <div>
                            <Text type="secondary" style={{ fontSize: "12px" }}>Удалено:</Text>
                            <Text strong style={{ color: "#ff4d4f", marginLeft: "8px" }}>
                              {change.oldQuantity}
                            </Text>
                          </div>
                        ) : (
                          <div>
                            <Text type="secondary" style={{ fontSize: "12px" }}>Добавлено:</Text>
                            <Text strong style={{ color: "#52c41a", marginLeft: "8px" }}>
                              {change.newQuantity}
                            </Text>
                          </div>
                        )}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Panel>
              ))}
            </Collapse>
          </div>
        )}
      </Modal>
    </Modal>
  );
} 